import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useRef } from "react";
import { useProducts } from "@/lib/api";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import ProductImage from "@/components/ProductImage";
import OrbitSection from "@/components/OrbitSection";
import Newsletter from "@/components/Newsletter";
import DropTimer from "@/components/DropTimer";

const CHAPTERS = [
  { n: "01", title: "THE PATH", body: "Everyone is handed a map. Most people follow it. aligndept is for the ones who fold the map, put it away, and walk by signal instead." },
  { n: "02", title: "THE FREQUENCY", body: "111. 222. 333. Numbers that show up twice and call it coincidence. We call it confirmation. The garments carry them quietly — for those who notice." },
  { n: "03", title: "THE ORDER", body: "Nothing arrives out of sequence. The delay was the route. The detour was the door. Right place. Right time. Stay aligned." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex h-screen min-h-[640px] flex-col justify-center overflow-hidden">
      <motion.div style={{ y: yBg }} className="absolute inset-0">
        <img
          data-testid="hero-campaign-image"
          src="/img/look-02-backtoback.webp"
          alt="aligndept campaign"
          className="absolute inset-0 h-full w-full object-cover opacity-25 grayscale"
        />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 65% 30%, rgba(26,26,32,0.6) 0%, #0a0a0c 65%)" }} />
        <motion.svg
          className="absolute -right-40 top-1/2 h-[130vh] w-[130vh] -translate-y-1/2 text-white/[0.06]"
          viewBox="0 0 500 500"
          fill="none"
          animate={{ rotate: 360 }}
          transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
        >
          <circle cx="250" cy="250" r="240" stroke="currentColor" strokeDasharray="2 8" />
          <circle cx="250" cy="250" r="180" stroke="currentColor" />
          <ellipse cx="250" cy="250" rx="240" ry="120" stroke="currentColor" />
          <ellipse cx="250" cy="250" rx="180" ry="70" stroke="currentColor" strokeDasharray="4 6" />
          <path d="M250 10v480M10 250h480" stroke="currentColor" strokeDasharray="1 10" />
        </motion.svg>
        <svg className="absolute left-[12%] bottom-[24%] h-5 w-5 text-bone/25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M12 2v20M2 12h20" /></svg>
        <svg className="absolute right-[22%] top-[16%] h-4 w-4 text-bone/25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><circle cx="12" cy="12" r="8" /><path d="M12 8v8M8 12h8" /></svg>
        <span className="absolute -left-6 top-1/2 hidden -translate-y-1/2 select-none font-display text-[11rem] font-extrabold leading-none text-bone/[0.035] lg:block">111</span>
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="font-mono text-[10px] tracking-[0.4em] text-fade"
        >
          EST. 2026 — NO COINCIDENCES
        </motion.p>
        <h1 className="mt-4">
          <span className="mask-line">
            <motion.span
              className="block font-display text-[18vw] font-extrabold lowercase leading-[0.85] tracking-tighter text-bone sm:text-[13vw] lg:text-[10rem]"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ delay: 0.35, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            >
              aligndept
            </motion.span>
          </span>
          <span className="mask-line mt-2">
            <motion.span
              className="block font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-bone/90 sm:text-6xl"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ delay: 0.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            >
              Stay aligned<span className="text-fade">.</span>
            </motion.span>
          </span>
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}>
          <Link
            to="/shop"
            data-testid="hero-shop-button"
            className="group mt-10 inline-flex items-center gap-3 border border-bone/40 px-8 py-4 font-display text-xs font-extrabold uppercase tracking-[0.25em] text-bone transition-all duration-300 hover:border-bone hover:bg-bone hover:text-ink"
          >
            Shop the collection
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2">
        <div className="flex flex-col items-center gap-2">
          <span className="font-mono text-[9px] tracking-[0.4em] text-fade">SCROLL</span>
          <motion.div className="h-8 w-px bg-bone/30" animate={{ scaleY: [1, 0.4, 1] }} transition={{ duration: 2, repeat: Infinity }} style={{ transformOrigin: "top" }} />
        </div>
      </motion.div>
    </section>
  );
}

export default function Home() {
  const { products } = useProducts();
  const newReleases = products.filter((p) => p.is_new).slice(0, 3);
  const shopPreview = products.slice(0, 4);

  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />

      <section data-testid="statement-section" className="px-5 py-28 sm:px-8 sm:py-40">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.35em] text-fade">A STATEMENT — 001</p>
            <h2 className="mt-6 font-display text-3xl font-extrabold uppercase leading-[1.02] tracking-tight text-bone sm:text-6xl lg:text-7xl">
              You are where
              <br />
              you're <span className="text-outline">supposed</span> to be.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-xl text-base font-light leading-relaxed text-neutral-400 sm:text-lg">
              aligndept exists for those who trust their path, embrace what makes them different, and understand that nothing happens by coincidence.
            </p>
            <Link to="/story" data-testid="statement-story-link" className="group mt-8 inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-bone/80 transition-colors hover:text-bone">
              READ THE STORY <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="manifesto-section" className="border-t border-white/10 px-5 py-24 sm:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.35em] text-fade">THE MESSAGE</p>
          </Reveal>
          <div className="mt-10 grid gap-px bg-white/10 lg:grid-cols-3">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12} className="bg-ink">
                <div data-testid={`manifesto-chapter-${c.n}`} className="group relative h-full p-8 transition-colors duration-500 hover:bg-coal sm:p-10">
                  <span className="absolute right-6 top-6 font-mono text-[10px] tracking-[0.3em] text-fade">CH.{c.n}</span>
                  <svg className="mb-8 h-5 w-5 text-bone/30 transition-colors group-hover:text-bone/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M12 4v16M4 12h16" />
                  </svg>
                  <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-bone">{c.title}</h3>
                  <p className="mt-5 text-sm font-light leading-relaxed text-neutral-400">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {newReleases.length > 0 && (
        <section data-testid="new-releases-section" className="border-t border-white/10 px-5 py-24 sm:px-8">
          <div className="mx-auto max-w-7xl">
            <Reveal className="flex items-end justify-between">
              <div>
                <p className="font-mono text-[10px] tracking-[0.35em] text-fade">DROP 001</p>
                <h2 className="mt-3 font-display text-4xl font-extrabold uppercase tracking-tight text-bone sm:text-6xl">New releases</h2>
              </div>
              <Link to="/collections" data-testid="new-releases-view-all" className="hidden items-center gap-2 font-mono text-[11px] tracking-[0.3em] text-bone/70 transition-colors hover:text-bone sm:flex">
                THE DROP <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Reveal>
            <Reveal className="mt-12">
              <Link to={`/product/${newReleases[0].id}`} data-testid="editorial-feature-0" className="group relative mx-auto block max-w-4xl">
                <ProductImage product={newReleases[0]} ratio="aspect-[4/3]" fit="contain" tag="LOOK 01" />
                <div className="mt-4 flex items-baseline justify-between">
                  <p className="font-display text-lg font-bold uppercase text-bone">{newReleases[0].name}</p>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-fade">{newReleases[0].angel} — THE SIGNAL</p>
                </div>
                {newReleases[0].drop_at && new Date(newReleases[0].drop_at) > new Date() && (
                  <div className="mt-6">
                    <DropTimer dropAt={newReleases[0].drop_at} label={`${newReleases[0].name} — FULL RELEASE`} />
                  </div>
                )}
              </Link>
              <p className="mx-auto mt-10 max-w-md text-center font-mono text-[10px] leading-relaxed tracking-[0.2em] text-fade">
                EVERY PIECE CARRIES A NUMBER. EVERY NUMBER CARRIES A MEANING. NOTHING HERE IS DECORATION.
              </p>
            </Reveal>
          </div>
        </section>
      )}

      <section data-testid="shop-preview-section" className="border-t border-white/10">
        <div className="flex items-end justify-between px-5 py-10 sm:px-8">
          <div>
            <p className="font-mono text-[10px] tracking-[0.35em] text-fade">THE CATALOG</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight text-bone sm:text-5xl">Shop</h2>
          </div>
          <Link to="/shop" data-testid="shop-preview-view-all" className="flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] text-bone/70 transition-colors hover:text-bone">
            VIEW ALL <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 border-t border-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {shopPreview.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      <OrbitSection />

      <section data-testid="lookbook-preview" className="relative overflow-hidden py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid items-end gap-8 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <div className="group relative">
                <ProductImage
                  product={{ id: "look-hero", name: "222 Longsleeve", angel: "222", image_url: "/img/look-02-backtoback.webp" }}
                  ratio="aspect-[4/5]"
                  tag="CAMPAIGN 001"
                />
              </div>
            </Reveal>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-bone sm:text-5xl">
                  Worn in
                  <br />
                  the world.
                </h2>
                <p className="mt-6 max-w-sm text-base font-light leading-relaxed text-neutral-400">
                  The lookbook is shot where the brand lives — parking structures at 2am, empty intersections, the last train. Places you end up on purpose.
                </p>
                <Link to="/lookbook" data-testid="lookbook-preview-link" className="group mt-8 inline-flex items-center gap-3 border border-bone/40 px-7 py-3.5 font-display text-xs font-extrabold uppercase tracking-[0.25em] text-bone transition-all hover:bg-bone hover:text-ink">
                  View lookbook <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1.5" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={["YOU ARE WHERE YOU'RE SUPPOSED TO BE", "111 — 222 — 333 — 444 — 555", "ALIGNDEPT"]} />
      <Newsletter />
    </div>
  );
}
